import { Div, RefString, Select, SelectOption, Span, Text, Link, ProgressBar, Snack } from '@tblabs/truffle';
import { IWatcher } from '../../Services/IWatcher';
import { PreviewBox } from '../../Components/PreviewBox';
import { Page } from "../Page";
import { Row } from '../../Components/General/Row';
import { StatusBox } from '../../Components/Status/StatusBox';
import { Status } from '../../Services/Status/Status';
import { StatusEvent } from '../../Services/Status/StatusEvent';
import { SnapshotsBox } from '../../Components/Snapshots/SnapshotsBox';
import { OcrBox } from '../../Components/Snapshots/OcrBox';
import { PrimaryButton } from '../../Components/General/Buttons/PrimaryButton';
import { DangerButton } from '../../Components/General/Buttons/DangerButton';
import { Translations } from '../../Translations/Translations';
import { StatusIconInfoWindow } from '../../Windows/StatusIconInfoWindow';
import { PreviewMode } from '../../Models/PreviewMode';
import { ReceiverLogic } from '../../Logic/ReceiverLogic';
import { SnapStorage } from '../../Services/Storage/SnapStorage';
import { Icon } from '../../Components/General/Icon';
import { MemoryCleanupWindow } from '../../Components/Snapshots/Windows/MemoryCleanupWindow';
import { SnapsSavingWindow } from '../../Components/Snapshots/Windows/SnapsSavingWindow';
import { CameraPicturePresetSelector } from "../../Services/Camera/Control/CameraPicturePresetSelector";
import { CameraPictureSettingsBox } from "../../Services/Camera/Control/CameraPictureSettingsBox";
import { StatusLed } from './StatusLed';
import { CameraOptionsMenu } from './CameraOptionsMenu';
import { HeaderMenuBox } from './HeaderMenuBox';
import { DeviceConfigForWatcher } from '../../Models/DeviceConfig';
import { IServer } from '../../Services/IServer';
import { IoLogic } from '../../Io/IoLogic';
import { AppSettings } from '../../Services/AppSettings';
import { ConnectionStatusEvent } from '../../Services/ConnectionStatusEvent';
import { KickedScreen } from '../../Components/Screens/KickedScreen';
import { LaunchingScreen } from '../../Components/Screens/LaunchingScreen';
import { IoBox } from '../../Io/Components/IoBox';
import { Beepers } from '../../Services/Beepers';
import { MalfunctionScreen } from '../../Components/Screens/MalfunctionScreen';
import { ICamera } from '../../Services/Camera/ICamera';


export class ReceiverPage extends Page
{
    private deviceId = new RefString("Connecting...");
    private readonly takeSnapshotButton: PrimaryButton;
    private readonly restorePreviewButton: DangerButton;
    private readonly previewBox: PreviewBox;
    private readonly snapshots: SnapshotsBox;
    private readonly ocrBox: OcrBox;
    private readonly statusLed: StatusLed;

    constructor(
        private _server: IServer,
        private _watcher: IWatcher,
        private _camera: ICamera,
        private _logic: ReceiverLogic,
        private _status: Status,
        private _storage: SnapStorage,
        private _lang: Translations,
        private _ioLogic: IoLogic,
        private _app: AppSettings,
        private _beepers: Beepers)
    {
        super(_lang.ReceiverPageTitle);

        _status.Set(StatusEvent.Launching);

        this.takeSnapshotButton = new PrimaryButton(this._lang.Save)
            .FloatLeft().Class("btn half take-snapshot-btn")
            .Enable(this._logic.AllowTakingSnapshot)
            .OnClick(() => this._logic.TakeSnapshot());
        this.restorePreviewButton = new DangerButton(this._lang.RestorePreview)
            .FloatRight().Class("btn half restore-preview-btn")
            .EnableWhen(this._logic.AllowRestorePreview)
            .OnClick(() => this._logic.LivePreviewMode());
        this.previewBox = new PreviewBox(this._logic.PreviewStream)
            .OnClick(() => this._logic.TakeSnapshot());
        this.snapshots = new SnapshotsBox(this._logic.Snapshots);
        this.ocrBox = new OcrBox(this._lang, this._logic);
        this.statusLed = new StatusLed(this._status).Class("status-led")
            .OnClick(() => new StatusIconInfoWindow(this._lang));
    }

    protected async OnRemove(): Promise<void>
    {
        this.statusLed.Destroy();
        this._camera.Destroy();
        this._server.Disconnect();
        this._ioLogic.Dispose();
        this._status.Dispose();
    }

    protected async OnAppend(): Promise<void>
    {
        const launchingInfo = new LaunchingScreen(this._lang);
        this.AddToBody(launchingInfo);
        
        this.Render();

        this.CameraEvents();
        this.ServerEvents(launchingInfo);
        this.WatcherEvents();

        this._server.Init();
        await this._storage.Init();
        await this._watcher.Init();
        await this._camera.Init();
        this._server.Connect();
    }

    private ServerEvents(launchingInfo: LaunchingScreen)
    {
        this._server.OnConnectionChange.Subscribe((status: ConnectionStatusEvent) =>
        {
            if (status.IsConnected)
                launchingInfo.RemoveSelf();
        });
    }

    private CameraEvents()
    {
        this._camera.OnConfigReceived.Subscribe((config: DeviceConfigForWatcher) =>
        {
            this.deviceId.value = config?.DeviceId ?? ".";

            this._status.SetDeviceId(config?.DeviceId);
        });

        this._camera.OnMalfunction.Subscribe((reason: string) =>
        {
            this._status.Set(StatusEvent.Malfunction);
            this.body.Append(new MalfunctionScreen(this._lang, reason));
        });
    }

    private WatcherEvents()
    {
        this._watcher.OnKick.Subscribe((reason: string) =>
        {
            this.body.Append(new KickedScreen(this._lang, reason));
        });

        this._watcher.OnMessage.Subscribe((message: string) =>
        {
            new Snack(this._lang.Message, message, { autoCloseTimeout: 0, showCloseButton: true });
        });
    }

    private Render(): void
    {
        this.AddToHeader(
            new Div().Append(
                this.statusLed,
                new Text(this.deviceId).Class('device-id'),
                new Span(this._server.ServerId).Class('server-id')
            ),
            new HeaderMenuBox(this._lang, this._app, this._beepers, this._camera),
        );

        this.AddToBody(
            new ProgressBar(this._logic.PictureControl.ThrottleProgressPercent),
            new Row(
                new Select(this._logic.Mode, [
                    new SelectOption(this._lang.LivePreview, PreviewMode.LivePreview,),
                    new SelectOption(this._lang.SnapshotView, PreviewMode.SnapshotView),
                ]).Class("camera-preview-mode-selector"),
                new CameraOptionsMenu(this._lang, this._logic),
                new CameraPicturePresetSelector(this._logic.PictureControl).FloatRight(),
            ).Class("preview-row"),
            new CameraPictureSettingsBox(this._lang, this._logic.PictureControl),
            new Row().Height(0).ClearBoth(),
            this.previewBox,
            new Row(
                this.takeSnapshotButton,
                this.restorePreviewButton
            ).Height(48),
            new Div().Append(
                new Span(this._lang.RowSnapshotsTitle).Class("title"),
                new Link(this._lang.Clear + "...").Color("#1e87f0").FloatRight()
                    .OnClick(() => new MemoryCleanupWindow(this._storage, this._lang)),
                new Icon("delete", "#c9c9c9").FloatRight(),
                new Link(this._lang.Save + "...").MarginRight(12).FloatRight()
                    .OnClick(() => new SnapsSavingWindow(this._storage, this._lang)),
                new Icon("cloud", "#c9c9c9").MarginRight(4).FloatRight(),
            ).MarginTop(8),
            this.snapshots,
            new IoBox(this._ioLogic, this._lang, this._app),
            this.ocrBox,
        );

        this.SetFooter(
            new StatusBox(this._server, this._camera, this._status),
        );
    }
}
